import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** Buy Now urgency window on PDP, guest address and checkout. */
export const GUEST_OFFER_DURATION_MS = 54 * 60 * 1000

export type GuestOfferTimerEntry = {
  startedAt: number
}

type GuestOfferTimerState = {
  timers: Record<string, GuestOfferTimerEntry>
  /**
   * When each slug was last added to the cart. The cart's own order can't say:
   * the server orders a signed-in cart, and the post-login merge reorders it.
   */
  cartAddedAt: Record<string, number>
  /**
   * Record a PDP visit. First visit for a slug starts the clock;
   * revisits leave `startedAt` alone so the countdown continues.
   */
  touchSlug: (slug: string) => void
  markAddedToCart: (slug: string) => void
  clear: () => void
}

/**
 * Remaining ms in the current 54-minute cycle.
 * Formula: `54m - ((now - startedAt) % 54m)` — exact cycle boundaries
 * show a full 54 minutes again.
 */
export function getRemainingMs(startedAt: number, now: number = Date.now()): number {
  const elapsed = Math.max(0, now - startedAt)
  return GUEST_OFFER_DURATION_MS - (elapsed % GUEST_OFFER_DURATION_MS)
}

export type CountdownUnits = [string, string, string]

export function getCountdownUnits(remainingMs: number): CountdownUnits {
  const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000))
  const hours = Math.floor(remainingSeconds / 3600)
  const minutes = Math.floor((remainingSeconds % 3600) / 60)
  const seconds = remainingSeconds % 60

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0'),
  ]
}

export const useGuestOfferTimerStore = create<GuestOfferTimerState>()(
  persist(
    (set, get) => ({
      timers: {},
      cartAddedAt: {},
      touchSlug: (slug) => {
        if (!slug) return
        if (get().timers[slug]) return
        set((state) => ({
          timers: {
            ...state.timers,
            [slug]: { startedAt: Date.now() },
          },
        }))
      },
      markAddedToCart: (slug) => {
        if (!slug) return
        set((state) => ({
          cartAddedAt: {
            ...state.cartAddedAt,
            [slug]: Date.now(),
          },
        }))
      },
      clear: () => set({ timers: {}, cartAddedAt: {} }),
    }),
    {
      name: 'guest-offer-timer-storage',
      partialize: (state) => ({ timers: state.timers, cartAddedAt: state.cartAddedAt }),
    },
  ),
)

if (typeof window !== 'undefined') {
  const clearGuestOfferTimers = () => {
    useGuestOfferTimerStore.getState().clear()
  }
  window.addEventListener('userLogout', clearGuestOfferTimers)
  window.addEventListener('userLogin', clearGuestOfferTimers)
}
